/**
 * Deterministic RNG.
 *
 * The bag is shuffled from a seed so that a match can be replayed exactly from
 * its action log — which is what makes the engine testable and lets the server
 * re-derive state after a restart without storing every tile position.
 */

/** xmur3 string hash -> 32-bit seed. */
function xmur3(str: string): () => number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
}

/** mulberry32 PRNG: small, fast, good enough for shuffling a tile bag. */
export function makeRng(seed: string): () => number {
  const seeder = xmur3(seed);
  let a = seeder();
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Fisher-Yates, in place, using the supplied RNG. */
export function shuffle<T>(items: T[], rng: () => number): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const a = items[i] as T;
    const b = items[j] as T;
    items[i] = b;
    items[j] = a;
  }
  return items;
}

interface MaybeCrypto {
  randomUUID?: () => string;
  getRandomValues?: <T extends ArrayBufferView>(a: T) => T;
}

/**
 * A random identifier that works outside a secure context.
 *
 * `crypto.randomUUID` is only exposed over HTTPS or on localhost. Served from
 * a LAN address over plain HTTP — which is exactly how you play this on the
 * same wifi, and how an Android emulator reaches the dev server — it is simply
 * missing, and calling it throws. `crypto.getRandomValues` has no such
 * restriction, so it carries the fallback, with `Math.random` behind that for
 * environments offering neither.
 */
export function uuid(): string {
  const c = (globalThis as { crypto?: MaybeCrypto }).crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  if (typeof c?.getRandomValues === 'function') {
    const b = c.getRandomValues(new Uint8Array(16));
    b[6] = ((b[6] as number) & 0x0f) | 0x40;      // version 4
    b[8] = ((b[8] as number) & 0x3f) | 0x80;      // variant 10
    const hex = [...b].map((n) => n.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-` +
           `${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}` +
         `-${Math.random().toString(36).slice(2)}`;
}

/**
 * A fresh, unguessable seed.
 *
 * Every match needs its own. Falling back to a fixed default means every table
 * is dealt the identical bag in the identical order, which is both unfair to
 * anyone who has played before and deeply strange the second time you see it.
 */
export const randomSeed = (): string => uuid();
